import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import NewsletterSection from '../components/NewsletterSection';
import Footer from '../components/Footer';
import ChatbotWidget from '../components/ChatbotWidget';
import { MapPin, Phone, Mail, Clock, Facebook, Twitter, Linkedin, Instagram, Youtube } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutOne92 = () => {
  const [settings, setSettings] = useState({
    about_text: '',
    contact_details: {
      address: '',
      phone: '',
      email: '',
      hours: ''
    },
    social_media: {
      facebook: { url: '', enabled: false },
      twitter: { url: '', enabled: false },
      linkedin: { url: '', enabled: false },
      instagram: { url: '', enabled: false },
      youtube: { url: '', enabled: false }
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/one92/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch One92 settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const socialLinks = [
    { platform: 'facebook', icon: Facebook, url: settings.social_media.facebook.url, enabled: settings.social_media.facebook.enabled, label: 'Facebook' },
    { platform: 'twitter', icon: Twitter, url: settings.social_media.twitter.url, enabled: settings.social_media.twitter.enabled, label: 'Twitter' },
    { platform: 'linkedin', icon: Linkedin, url: settings.social_media.linkedin.url, enabled: settings.social_media.linkedin.enabled, label: 'LinkedIn' },
    { platform: 'instagram', icon: Instagram, url: settings.social_media.instagram.url, enabled: settings.social_media.instagram.enabled, label: 'Instagram' },
    { platform: 'youtube', icon: Youtube, url: settings.social_media.youtube.url, enabled: settings.social_media.youtube.enabled, label: 'YouTube' }
  ].filter(link => link.enabled && link.url);

  if (loading) {
    return (
      <>
        <Header />
        <div style={{ 
          minHeight: '60vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <p className="body-medium">Loading...</p>
        </div>
        <Footer />
        <ChatbotWidget />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="about-one92-page">
        <div className="container">
          {/* Hero Section */}
          <section className="about-hero">
            <h1 className="about-title">About One92 Communication Services</h1>
            <div className="about-subtitle">
              <p className="body-medium">Parent Organization to Visaasure</p>
            </div>
          </section>

          {/* About Content */}
          <section className="about-content">
            <div className="content-card">
              {settings.about_text.split('\n\n').map((paragraph, index) => (
                <p key={index} className="body-medium about-paragraph">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="contact-section">
            <h2 className="section-heading">Get in Touch</h2>
            <div className="contact-grid">
              {settings.contact_details.address && (
                <div className="contact-card">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <h3 className="contact-label">Address</h3>
                  <p className="body-small">{settings.contact_details.address}</p>
                </div>
              )}
              
              {settings.contact_details.phone && (
                <div className="contact-card">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <h3 className="contact-label">Phone</h3>
                  <p className="body-small">{settings.contact_details.phone}</p>
                </div>
              )}
              
              {settings.contact_details.email && (
                <div className="contact-card">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <h3 className="contact-label">Email</h3>
                  <p className="body-small">{settings.contact_details.email}</p>
                </div>
              )}
              
              {settings.contact_details.hours && (
                <div className="contact-card">
                  <div className="contact-icon">
                    <Clock size={24} />
                  </div>
                  <h3 className="contact-label">Business Hours</h3>
                  <p className="body-small">{settings.contact_details.hours}</p>
                </div>
              )}
            </div>
          </section>

          {/* Social Media Section */}
          {socialLinks.length > 0 && (
            <section className="social-section">
              <h2 className="section-heading">Connect With Us</h2>
              <div className="social-links">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label={link.label}
                    >
                      <Icon size={24} />
                      <span>{link.label}</span>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {/* Closing Statement */}
          <section className="closing-section">
            <div className="closing-card">
              <p className="body-large closing-text">
                Together with Visaasure, One92 Communication Services is committed to excellence in digital communication and operational support—empowering global mobility with trust and transparency.
              </p>
            </div>
          </section>
        </div>
      </div>
      <NewsletterSection />
      <Footer />
      <ChatbotWidget />
    </>
  );
};

export default AboutOne92;
